import { Hint } from "./Hint";
import { NaveIcon } from "./NavePage";

const pad = (value: number): string => String(value).padStart(2, "0");
export const isoDate = (year: number, month: number, day: number): string => `${year}-${pad(month + 1)}-${pad(day)}`;

export type CalendarMonth = Readonly<{ year: number; month: number }>;

/**
 * Accessible month grid. Only days present in `availableDates` can be picked; the rest stay
 * disabled. Dates are plain YYYY-MM-DD strings, so the grid never depends on the browser's time zone.
 */
export const MonthCalendar = ({ cursor, availableDates, selected, today, holidays, canGoBack, canGoForward, locale, previousLabel, nextLabel, onSelect, onMonthChange }: {
  cursor: CalendarMonth;
  availableDates: ReadonlySet<string>;
  selected?: string | undefined;
  today?: string | undefined;
  holidays?: ReadonlyMap<string, string> | undefined;
  canGoBack: boolean;
  canGoForward: boolean;
  locale: string;
  previousLabel: string;
  nextLabel: string;
  onSelect(date: string): void;
  onMonthChange(delta: -1 | 1): void;
}) => {
  const title = new Intl.DateTimeFormat(locale, { month: "long", year: "numeric", timeZone: "UTC" }).format(new Date(Date.UTC(cursor.year, cursor.month, 1)));
  const weekdays = Array.from({ length: 7 }, (_, index) => new Intl.DateTimeFormat(locale, { weekday: "short", timeZone: "UTC" }).format(new Date(Date.UTC(2023, 0, 1 + index))));
  const full = new Intl.DateTimeFormat(locale, { dateStyle: "full", timeZone: "UTC" });
  const leading = new Date(Date.UTC(cursor.year, cursor.month, 1)).getUTCDay();
  const count = new Date(Date.UTC(cursor.year, cursor.month + 1, 0)).getUTCDate();
  const cells: (number | undefined)[] = [...Array<undefined>(leading).fill(undefined), ...Array.from({ length: count }, (_, index) => index + 1)];
  while (cells.length % 7 !== 0) cells.push(undefined);
  return (
    <div className="nave-cal">
      <div className="nave-cal-head">
        <button type="button" className="nave-cal-nav" aria-label={previousLabel} disabled={!canGoBack} onClick={() => onMonthChange(-1)}><NaveIcon name="lucide:chevron-left" /></button>
        <strong className="nave-cal-title" aria-live="polite">{title}</strong>
        <button type="button" className="nave-cal-nav" aria-label={nextLabel} disabled={!canGoForward} onClick={() => onMonthChange(1)}><NaveIcon name="lucide:chevron-right" /></button>
      </div>
      <div className="nave-cal-grid" role="group" aria-label={title}>
        {weekdays.map((name) => <span key={name} className="nave-cal-weekday" aria-hidden="true">{name}</span>)}
        {cells.map((day, index) => {
          if (day === undefined) return <span key={`blank-${index}`} className="nave-cal-blank" />;
          const date = isoDate(cursor.year, cursor.month, day);
          const holiday = holidays?.get(date);
          const available = holiday === undefined && availableDates.has(date);
          const button = <button key={date} type="button" className="nave-cal-day" disabled={!available} aria-pressed={selected === date}
            aria-label={full.format(new Date(`${date}T00:00:00Z`))} data-available={available ? "true" : "false"} data-holiday={holiday === undefined ? "false" : "true"} data-today={date === today ? "true" : "false"} onClick={() => onSelect(date)}>{day}</button>;
          return holiday === undefined ? button : <Hint key={date} label={holiday}>{button}</Hint>;
        })}
      </div>
    </div>
  );
};
